import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "@i18n";
import useBoolean from "@hooks/useBoolean";
import { twMerge } from "tailwind-merge";
import { Icon } from "@syarahtech/ui";
import SAR from "@componentsShared/SAR/SAR";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import DrawerPaymentInfo from "@/pages/checkout/components/PaymentInfo/DrawerPaymentInfo";
import CarInfoShimmer from "@/pages/checkout/components/HeadingSteps/MobileCarInfo/CarInfoShimmer";
/**
 * Amwal-owned replacement for the shared `MobileCarInfoV2`: same car-info
 * trigger row, but the price-breakdown sheet (Figma mobile Pricing node
 * 333:15571) opens in the shared `BottomSheet` primitive instead of the
 * legacy vaul drawer. The sheet body stays the shared `DrawerPaymentInfo`
 * (internals untouched) — the 2026 palette is applied from the outside via
 * descendant className overrides only:
 *   - `#484848` labels/values → navy `#00143c`
 *   - `#6c7a8d` / `#6D7A8D` helper notes → `#6b7ea5`
 *   - title underline + rows-container/deposit top rules hidden (the total
 *     row keeps its own `#D6DFE9` separators, matching the design)
 */
var AmwalMobileCarInfo = function AmwalMobileCarInfo(_ref) {
  var _checkoutOrderV2$sect, _checkoutOrderV2$sect2, _checkoutOrderV2$sect3, _carHeader$car_image, _checkoutOrderV2$post, _checkoutOrderV2$post2, _checkoutOrderV2$post3;
  var checkoutOrderV2 = _ref.checkoutOrderV2,
    hideData = _ref.hideData,
    className = _ref.className,
    _onClickInfoIcon = _ref.onClickInfoIcon;
  var _useTranslation = useTranslation(["web_checkout_cash"]),
    tShort = _useTranslation.tShort;
  var sheet = useBoolean();
  var carHeader = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$sect = checkoutOrderV2.sections) === null || _checkoutOrderV2$sect === void 0 ? void 0 : (_checkoutOrderV2$sect2 = _checkoutOrderV2$sect.find(function (s) {
    return s.type === "car_header";
  })) === null || _checkoutOrderV2$sect2 === void 0 ? void 0 : (_checkoutOrderV2$sect3 = _checkoutOrderV2$sect2.items) === null || _checkoutOrderV2$sect3 === void 0 ? void 0 : _checkoutOrderV2$sect3[0];
  var carImage = (_carHeader$car_image = carHeader === null || carHeader === void 0 ? void 0 : carHeader.car_image) !== null && _carHeader$car_image !== void 0 ? _carHeader$car_image : checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$post = checkoutOrderV2.post) === null || _checkoutOrderV2$post === void 0 ? void 0 : _checkoutOrderV2$post.make_logo;
  var carTitle = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$post2 = checkoutOrderV2.post) === null || _checkoutOrderV2$post2 === void 0 ? void 0 : _checkoutOrderV2$post2.post_title;
  var totalPrice = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$post3 = checkoutOrderV2.post) === null || _checkoutOrderV2$post3 === void 0 ? void 0 : _checkoutOrderV2$post3.post_price;
  if (!checkoutOrderV2) {
    return /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement(CarInfoShimmer, null));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: twMerge("w-full flex justify-between items-center gap-2 bg-white px-3 py-2", className)
  }, carImage && /*#__PURE__*/React.createElement("img", {
    src: carImage,
    alt: carTitle !== null && carTitle !== void 0 ? carTitle : "",
    className: "h-[57px] w-[66px] shrink-0 rounded-lg border border-solid border-[#E9E9E9] object-cover"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 items-center gap-2 justify-between overflow-hidden"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5 min-w-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-start text-[#00143C] font-bold text-xs leading-5 line-clamp-2"
  }, carTitle)), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-1 cursor-pointer shrink-0",
    onClick: sheet.setTrue
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-drop-down-rounded",
    width: 7,
    height: 7,
    className: twMerge("text-primary-new transition-transform duration-200", sheet.value ? "rotate-180" : "rotate-0")
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-1"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-base text-primary-new font-bold"
  }, totalPrice), /*#__PURE__*/React.createElement(SAR, {
    className: "text-primary-new"
  }))))), /*#__PURE__*/React.createElement(BottomSheet, {
    open: sheet.value,
    onClose: sheet.setFalse,
    title: tShort("drawer_payment/price_details", {
      ns: "web_checkout_cash"
    }),
    header: /*#__PURE__*/React.createElement("div", {
      className: "flex px-4 pb-1 pt-1"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: sheet.setFalse,
      className: "flex h-7 w-7 cursor-pointer items-center justify-center border-none bg-transparent text-[#00143c] transition-opacity hover:opacity-70"
    }, /*#__PURE__*/React.createElement(Icon, {
      icon: "material-symbols--close",
      width: 5,
      height: 5
    }))),
    className: twMerge("max-h-[85vh]", "[&_[class*='484848']]:!text-[#00143c]", "[&_[class*='6c7a8d']]:!text-[#6b7ea5]", "[&_[class*='6D7A8D']]:!text-[#6b7ea5]", "[&_strong[class*='D6DFE9']]:!border-b-transparent", "[&_[class*='d6dfe9']]:!border-t-transparent")
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-1 overflow-y-auto"
  }, /*#__PURE__*/React.createElement(DrawerPaymentInfo, {
    checkoutOrderV2: checkoutOrderV2,
    hideData: hideData,
    onClickInfoIcon: function onClickInfoIcon() {
      sheet.setFalse();
      _onClickInfoIcon === null || _onClickInfoIcon === void 0 ? void 0 : _onClickInfoIcon();
    }
  }))));
};
export default AmwalMobileCarInfo;